// src/rms/context/RmsContext.js
// ============================================================================
// Operator session + resolved vertical config
// ============================================================================
// Every RMS screen reads its labels, fields and workflow rules from here rather
// than hardcoding them, which is what makes one screen serve twelve trades.
//
// `t(key, fallback)` resolves a terminology token: t("assetPlural") returns
// "Vehicles" for a car operator and "Units" for a storage operator.
// ============================================================================

import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import db from "../data";
import seedOperator from "../data/seed";
import { ensureTemplates } from "../data/agreementService";
import { provisionTaxDefaults } from "../data/taxService";
import { configFor } from "../data/rentalService";
import { capabilitiesFor } from "../data/permissions";
import {
  getLocale,
  setLocale as setActiveLocale,
  chooseLocale,
  readStoredLocale,
  detectLocale,
  resolveLocale,
  subscribeToLocale,
  applyDocumentLocale,
  isRtl,
} from "../i18n/locale";

/**
 * Languages where lowercasing a noun is a spelling error rather than a style
 * choice. German capitalises all nouns; there is no second entry today, and a
 * set rather than an `=== "de"` is so adding one is a data change.
 */
const NEVER_LOWERCASED = new Set(["de"]);

const RmsContext = createContext(null);

export function RmsProvider({ children }) {
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped after any write so screens re-read without a global store.
  const [revision, setRevision] = useState(0);
  const [locale, setLocaleState] = useState(getLocale);
  // Re-renders this provider (and therefore every screen) when the catalogue
  // changes, so a language switch does not leave stale words behind.
  const { t: translate } = useTranslation();

  // The locale lives in module state so plain service functions can format
  // without a hook; this keeps React re-rendering in step with it.
  useEffect(() => subscribeToLocale(setLocaleState), []);

  // Before an operator exists -- the landing page, the sign-in screen, the
  // vertical picker -- fall back to what the person chose last, then to what
  // their browser asks for.
  useEffect(() => {
    const initial = readStoredLocale() || detectLocale();
    setActiveLocale(initial);
    applyDocumentLocale(initial);
  }, []);

  // Once a business is loaded, its default applies -- unless this person has
  // already chosen for themselves, in which case their choice wins. That is the
  // whole point of ui_locale being separate from default_locale.
  useEffect(() => {
    if (!operator) return;
    const chosen = readStoredLocale();
    if (chosen) return;
    const fromOperator = resolveLocale(operator.default_locale);
    if (fromOperator) setActiveLocale(fromOperator);
  }, [operator]);

  // The signed-in person's own member row. Loaded by the same lifecycle as the
  // operator and invalidated by the same `revision`, so a role change on the
  // staff screen is reflected everywhere without a reload.
  const [member, setMember] = useState(null);
  // Tracked separately from `loading`, which reports only the OPERATOR.
  //
  // The member row is fetched in its own effect, which cannot start until the
  // operator exists -- so there is a window where the operator has arrived,
  // `loading` is already false, and the role is still unknown. Anything that
  // decides access from `can` during that window decides it from an empty set
  // and concludes the answer is no.
  //
  // On the browser backend that window is roughly zero: the member is a
  // synchronous localStorage read. Against Supabase it is a network round trip,
  // which is how /billing came to render for one frame and then bounce back to
  // the dashboard on the live site while behaving perfectly in development.
  //
  // Tracked as "which operator has the member state been resolved FOR", not as
  // a boolean set inside the effect. A boolean cannot close the window: the
  // effect that would raise it runs AFTER the render in which `loading` flips
  // to false, so there is exactly one render where both flags read false and
  // the member is still null -- and one render is all a <Navigate> needs. That
  // is why the first attempt at this fix worked in the browser backend and
  // still bounced on a cold load. Deriving it from the operator id has no such
  // window, because there is no moment where it has not been computed.
  //
  // It deliberately does NOT reset when `revision` changes. A refresh re-reads
  // the member, but the role we already have is still the right one to gate
  // with meanwhile -- blanking it would flash every screen on every save.
  const [memberFor, setMemberFor] = useState(null);

  /** Every business this account can reach, for the switcher. */
  const [operators, setOperators] = useState([]);

  const refresh = useCallback(async () => {
    const listed = await db.listOperatorsForUser();
    setOperators(listed.success ? listed.data || [] : []);

    const result = await db.getOperatorForUser();
    setOperator(result.success ? result.data : null);
    setLoading(false);
    return result.data;
  }, []);

  /**
   * Move this session to another business.
   *
   * A full page load rather than a state update. Every screen holds rows for
   * the operator it mounted under -- assets, reservations, the ad rail's
   * pools -- and reconciling all of that in place is a long list of chances to
   * show one business's data under another's name. Switching is rare; being
   * certain is worth more than being smooth.
   */
  const switchOperator = useCallback(async (operatorId) => {
    if (!operatorId || operatorId === operator?.id) return;
    await db.setActiveOperator(operatorId);
    window.location.assign("/rms");
  }, [operator]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!operator?.id) {
      setMember(null);
      setMemberFor(null);
      return;
    }
    let cancelled = false;
    db.getCurrentMember(operator.id).then((r) => {
      if (cancelled) return;
      setMember(r.success ? r.data : null);
      setMemberFor(operator.id);
    });
    return () => {
      cancelled = true;
    };
  }, [operator, revision]);

  // No operator means there is no role to wait for, and a gate that waits
  // forever is worse than one that answers.
  const memberLoading = operator?.id ? memberFor !== operator.id : false;

  /** Provisions a new operator from a vertical preset and seeds demo data. */
  const provision = useCallback(
    async ({
      verticalKey,
      businessName,
      currency,
      country,
      defaultLocale,
      pricesIncludeTax = false,
      taxes = [],
    }) => {
      const result = await db.createOperator({
        vertical_key: verticalKey,
        business_name: businessName,
        currency,
        country,
        default_locale: defaultLocale,
        prices_include_tax: !!pricesIncludeTax,
      });
      if (!result.success) return result;

      // Before the demo data, so the sample invoice it raises is priced with
      // the operator's own tax rather than the seed's placeholder rates.
      await provisionTaxDefaults(result.data, taxes);
      await seedOperator(result.data);

      // The starter contracts. Here as well as on the Agreements tab, and the
      // duplication is deliberate: this catches every business created from now
      // on, the tab catches every business created before it existed. Writing
      // them is owner-gated, and the person provisioning is by definition the
      // owner -- a staff member arriving later could not install them.
      //
      // NOT inside seedOperator: everything that helper writes is stamped
      // is_demo, and "remove the demo data" must never delete the contract a
      // business hands its customers.
      await ensureTemplates(result.data);

      // Re-read: seeding patches the operator row (operational defaults), so
      // the copy returned by the insert is already stale by this point.
      const fresh = (await db.getOperatorForUser()).data || result.data;
      setOperator(fresh);
      setRevision((r) => r + 1);
      return result;
    },
    []
  );

  const updateOperator = useCallback(
    async (patch) => {
      if (!operator) return { success: false, error: "No operator loaded.", data: null };
      const result = await db.updateOperator(operator.id, patch);
      if (result.success) {
        setOperator(result.data);
        setRevision((r) => r + 1);
      }
      return result;
    },
    [operator]
  );

  /** Call after any mutation so dependent screens re-query. */
  const invalidate = useCallback(() => setRevision((r) => r + 1), []);

  const config = useMemo(() => configFor(operator), [operator]);

  /**
   * The one function that answers "what words do I use here".
   *
   * It handles two questions that used to be separate and are really the same
   * one, so that no screen has to hold two different translate functions and
   * remember which is which:
   *
   *   t("assetPlural")     per-VERTICAL wording: "Vehicles" for a car
   *                        operator, "Units" for a storage operator
   *   t("nav.dashboard")   per-LANGUAGE wording from the catalogue
   *
   * They are told apart by shape. Terminology tokens are bare camelCase and are
   * declared in the vertical's own preset; catalogue keys are dotted. The two
   * namespaces cannot collide, and every existing t("fleet", "Inventory") call
   * site keeps working untouched.
   *
   * English terminology is NOT duplicated into the English catalogue -- it
   * lives in presets.js, which stays its source of truth. A translated
   * catalogue overrides it with term.<vertical>.<token>, falling back to
   * term.base.<token> for wording shared across trades, and finally to the
   * preset's own English. So a language with no terminology translated yet
   * shows English nouns inside an otherwise translated screen, which is
   * readable, rather than raw keys, which is not.
   */
  const t = useCallback(
    (key, fallback = "", params) => {
      const isTerminology = typeof key === "string" && !key.includes(".");

      if (isTerminology && config?.terminology?.[key] !== undefined) {
        const scoped = `term.${config.key}.${key}`;
        if (i18n.exists(scoped)) return translate(scoped);
        const base = `term.base.${key}`;
        if (i18n.exists(base)) return translate(base);
        return config.terminology[key];
      }

      if (isTerminology) return fallback || key;

      // The options object carries ICU parameters, and it has to reach
      // i18next or a message like "Search {items}…" renders with the braces
      // still in it. Passing them through the options form rather than
      // i18next's positional defaultValue argument keeps both working at once.
      return translate(key, { defaultValue: fallback || key, ...(params || {}) });
    },
    [config, translate]
  );

  /**
   * A terminology noun, lowercased for use inside a sentence.
   *
   * Replaces `tLower("assetSingular")`, which was wrong in two of the
   * thirteen languages shipped:
   *
   *   * German capitalises EVERY noun, not just proper ones. "das fahrzeug" is
   *     simply a spelling mistake, so German is excluded outright.
   *   * Turkish has a dotless i: naive toLowerCase() turns "I" into "i" rather
   *     than "ı" and changes the word. Passing the locale to
   *     toLocaleLowerCase fixes that, and would fix any future locale with the
   *     same property, which is why the locale is threaded through rather than
   *     special-cased.
   *
   * Arabic, Hindi, Bengali, Urdu, Marathi and Japanese have no letter case at
   * all, so this is a no-op there and costs nothing.
   */
  const tLower = useCallback(
    (key, fallback = "") => {
      const word = t(key, fallback);
      if (NEVER_LOWERCASED.has(locale)) return word;
      try {
        return word.toLocaleLowerCase(locale);
      } catch {
        return word.toLowerCase();
      }
    },
    [t, locale]
  );

  // The trade's own name and pitch ("Construction Equipment", "Excavators,
  // lifts…"). These live in presets.js like the terminology, and are translated
  // the same way -- catalogue first, English preset as the fallback.
  const verticalLabel = useMemo(
    () => (config ? t(`vertical.${config.key}.label`, config.label) : ""),
    [config, t]
  );
  const verticalTagline = useMemo(
    () => (config ? t(`vertical.${config.key}.tagline`, config.tagline) : ""),
    [config, t]
  );

  // Depends on `locale` as well as the operator so a language change re-renders
  // every figure on screen rather than leaving stale ones behind.
  //
  // `currencyOverride` exists because rows carry their own currency and it is
  // not always the operator's current one. Invoices, reservations and payments
  // all store the currency they were raised in; formatting those with whatever
  // the operator happens to be set to today silently restates history the
  // moment anyone changes the setting. Pass the row's own currency for any
  // figure that came off a row.
  const formatMoney = useCallback(
    (cents, currencyOverride) => {
      const value = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(getLocale(), {
          style: "currency",
          currency: currencyOverride || operator?.currency || "USD",
          maximumFractionDigits: value % 1 === 0 ? 0 : 2,
        }).format(value);
      } catch {
        return `$${value.toFixed(2)}`;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operator, locale]
  );

  /** Changes the interface language and remembers it. See i18n/locale.js. */
  const changeLocale = useCallback((code) => chooseLocale(code), []);

  /**
   * What this person may do.
   *
   * Courtesy, not security -- RLS is the boundary, and every capability here
   * has a counterpart in the policies added by 20260820090001. A screen that
   * consults `can` is being polite about not offering a button whose save the
   * database would refuse.
   *
   * Defaults to the empty set while the member row is still loading, so the
   * first paint hides privileged controls rather than flashing them.
   */
  const can = useMemo(() => capabilitiesFor(member?.role), [member]);

  const value = useMemo(
    () => ({
      operator,
      config,
      loading,
      revision,
      member,
      memberLoading,
      operators,
      switchOperator,
      role: member?.role || null,
      can,
      isContinuous: config?.occupancyModel === "continuous",
      workflow: config?.workflow || {},
      t,
      tLower,
      formatMoney,
      locale,
      changeLocale,
      isRtl: isRtl(locale),
      verticalLabel,
      verticalTagline,
      refresh,
      provision,
      updateOperator,
      invalidate,
    }),
    [operator, operators, switchOperator, config, loading, revision, member, memberLoading, can, t, tLower, formatMoney, locale, changeLocale, verticalLabel, verticalTagline, refresh, provision, updateOperator, invalidate]
  );

  return <RmsContext.Provider value={value}>{children}</RmsContext.Provider>;
}

export function useRms() {
  const ctx = useContext(RmsContext);
  if (!ctx) throw new Error("useRms must be used inside <RmsProvider>");
  return ctx;
}

export default RmsContext;
